import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, CheckCircle2, AlertCircle, ArrowLeft, Eye, X, Database, Sparkles, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Card, { CardHeader, CardBody, CardFooter } from '../ui/Card';
import Spinner from '../ui/Spinner';
import { FileAnalysisCard } from './FileAnalysisCard';

interface DataFilesStageProps {
  caseId: string;
  onComplete: () => void;
  onBack: () => void;
}

interface EnterpriseSystem {
  appId: string;
  name: string;
  businessUseId: string;
  businessUse: string;
  generalDescription: string;
}

interface DataFile {
  fileId: string;
  filename: string;
  fileSize: number;
  fileType: string;
  columnCount?: number;
  rowCount?: number;
  sourceSystemName?: string;
  sourceSystemType?: string;
  uploadedAt: string;
}

export default function DataFilesStage({  caseId, onComplete, onBack }: DataFilesStageProps) {
  const queryClient = useQueryClient();
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, string[]>>({});
  const [analyzingFiles, setAnalyzingFiles] = useState<Record<string, any>>({});
  const [fileAnalyses, setFileAnalyses] = useState<Record<string, any>>({});
  const [analysisErrors, setAnalysisErrors] = useState<Record<string, string>>({});
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null);

  // Fetch system landscape to get enterprise systems
  const { data: systemLandscape, isLoading: isLoadingLandscape } = useQuery({
    queryKey: ['systemLandscape', caseId],
    queryFn: async () => {
      const response = await api.get(`/cases/${ caseId}/system-landscape`);
      return response;
    },
    enabled: !!caseId,
  });

  const systems: EnterpriseSystem[] = systemLandscape?.systems || [];

  // Fetch uploaded files
  const { data: filesResponse } = useQuery({
    queryKey: ['dataFiles', caseId],
    queryFn: async () => {
      try {
        const response = await api.get(`/data-files/cases/${ caseId}/files`);
        return response;
      } catch (error: any) {
        if (error?.response?.status === 404) return { files: [], total: 0 };
        throw error;
      }
    },
    enabled: !!caseId,
  });

  const files: DataFile[] = filesResponse?.files || [];

  // Fetch project schema for AI analysis
  const { data: schemaData } = useQuery({
    queryKey: ['schema', caseId],
    queryFn: async () => {
      try {
        const response = await api.get(`/schema-design/cases/${ caseId}/schema`);
        return response;
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
  });

  // AI analysis function
  const analyzeFile = async (file: File, fileId: string, systemName: string) => {
    if (!schemaData?.schema) {
      console.warn('No schema available for AI analysis');
      return null;
    }

    try {
      setAnalyzingFiles(prev => ({ ...prev, [fileId]: true }));
      setAnalysisErrors(prev => {
        const updated = { ...prev };
        delete updated[fileId];
        return updated;
      });

      // First, check if analysis already exists
      try {
        const existingResponse = await fetch(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/${fileId}/analysis`
        );

        if (existingResponse.ok) {
          const existingAnalysis = await existingResponse.json();
          console.log(`✅ Found existing analysis for ${file.name}`);
          setFileAnalyses(prev => ({ ...prev, [fileId]: existingAnalysis }));
          toast.success(`Loaded existing analysis for ${file.name}`);
          return existingAnalysis;
        }
      } catch (error) {
        // No existing analysis, continue with new analysis
        console.log(`No existing analysis for ${file.name}, running AI analysis`);
      }

      // No existing analysis, run AI analysis
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caseId', caseId);
      formData.append('fileId', fileId);  // Pass fileId so backend can save the analysis
      formData.append('sourceSystem', systemName);
      formData.append('sourceSystemType', 'ERP'); // TODO: Get from system metadata
      formData.append('schema', JSON.stringify(schemaData.schema));

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/analyze`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || response.statusText);
      }

      const analysis = await response.json();

      setFileAnalyses(prev => ({ ...prev, [fileId]: analysis }));
      toast.success(`AI analysis complete for ${file.name}`);

      return analysis;
    } catch (error: any) {
      console.error('AI analysis error:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      setAnalysisErrors(prev => ({ ...prev, [fileId]: errorMessage }));
      toast.error(`AI analysis failed: ${errorMessage}`);
      return null;
    } finally {
      setAnalyzingFiles(prev => ({ ...prev, [fileId]: false }));
    }
  };

  // Upload file mutation with source system auto-population and AI analysis
  const uploadMutation = useMutation({
    mutationFn: async ({ file, systemName }: { file: File; systemName: string }) => {
      const uploadedFile = await api.upload(`/data-files/cases/${ caseId}/upload`, file) as DataFile;

      // Immediately update source system
      await api.patch(
        `/data-files/cases/${ caseId}/files/${uploadedFile.fileId}/source-system`,
        {
          sourceSystemName: systemName,
          sourceSystemType: 'ERP' // Default - could be enhanced to detect from system info
        }
      );

      return { uploadedFile, file, systemName };
    },
    onSuccess: async ({ uploadedFile, file, systemName }) => {
      toast.success(`${file.name} uploaded successfully`);
      queryClient.invalidateQueries({ queryKey: ['dataFiles', caseId] });
      setUploadingFiles(prev => ({
        ...prev,
        [systemName]: prev[systemName]?.filter(name => name !== file.name) || []
      }));

      // Trigger AI analysis after successful upload
      const analysisResult = await analyzeFile(file, uploadedFile.fileId, systemName);

      // If analysis failed, expand the file so user sees the error
      if (!analysisResult) {
        setExpandedFileId(uploadedFile.fileId);
      }
    },
    onError: (error: any, { file, systemName }) => {
      toast.error(error?.response?.data?.detail || `Failed to upload ${file.name}`);
      setUploadingFiles(prev => ({
        ...prev,
        [systemName]: prev[systemName]?.filter(name => name !== file.name) || []
      }));
    },
  });

  // Delete file mutation
  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      return await api.delete(`/data-files/cases/${ caseId}/files/${fileId}`);
    },
    onSuccess: () => {
      toast.success('File deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['dataFiles', caseId] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete file');
    },
  });

  // Re-analyze a file (for retry after error)
  const handleReanalyze = async (file: DataFile) => {
    if (!schemaData?.schema) {
      toast.error('Schema not available for analysis');
      return;
    }

    setAnalyzingFiles(prev => ({ ...prev, [file.fileId]: true }));
    setAnalysisErrors(prev => {
      const updated = { ...prev };
      delete updated[file.fileId];
      return updated;
    });

    try {
      // Call backend re-analyze endpoint
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/${file.fileId}/reanalyze`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            caseId: caseId,
            sourceSystem: file.sourceSystemName || 'Unknown',
            sourceSystemType: file.sourceSystemType || 'ERP',
            schema: schemaData.schema,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Re-analysis failed');
      }

      const analysis = await response.json();
      setFileAnalyses(prev => ({ ...prev, [file.fileId]: analysis }));
      toast.success(`Re-analysis complete for ${file.filename}`);
    } catch (error: any) {
      console.error('Re-analysis error:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      setAnalysisErrors(prev => ({ ...prev, [file.fileId]: errorMessage }));
      toast.error(`Re-analysis failed: ${errorMessage}`);
    } finally {
      setAnalyzingFiles(prev => ({ ...prev, [file.fileId]: false }));
    }
  };

  // View AI analysis for a file
  const handleViewAnalysis = async (file: DataFile) => {
    // Toggle expansion
    if (expandedFileId === file.fileId) {
      setExpandedFileId(null);
      return;
    }

    setExpandedFileId(file.fileId);

    // If analysis already loaded in memory, just expand
    if (fileAnalyses[file.fileId]) {
      return;
    }

    // Try to load existing analysis from backend
    setAnalyzingFiles(prev => ({ ...prev, [file.fileId]: true }));

    try {
      const existingResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/${file.fileId}/analysis`
      );

      if (existingResponse.ok) {
        const existingAnalysis = await existingResponse.json();
        console.log(`✅ Loaded existing analysis for ${file.filename}`);
        setFileAnalyses(prev => ({ ...prev, [file.fileId]: existingAnalysis }));
      } else {
        // No analysis found - file was uploaded before AI feature was added
        console.log(`No existing analysis found for ${file.filename}`);
      }
    } catch (error: any) {
      console.error('Failed to load analysis:', error);
      toast.error(`Failed to load analysis: ${error.message}`);
    } finally {
      setAnalyzingFiles(prev => ({ ...prev, [file.fileId]: false }));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // System-specific file upload component
  const SystemUploadCard = ({ system }: { system: EnterpriseSystem }) => {
    const systemFiles = files.filter(f => f.sourceSystemName === system.name);
    const isUploading = (uploadingFiles[system.name] || []).length > 0;

    const onDrop = useCallback((acceptedFiles: File[]) => {
      setUploadingFiles(prev => ({
        ...prev,
        [system.name]: [...(prev[system.name] || []), ...acceptedFiles.map(f => f.name)]
      }));

      acceptedFiles.forEach(file => {
        uploadMutation.mutate({ file, systemName: system.name });
      });
    }, [system.name]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        'text/csv': ['.csv'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/json': ['.json'],
      },
    });

    return (
      <Card key={system.appId} className="border-l-4 border-l-primary-500">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Database className="w-5 h-5 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900">{system.name}</h3>
              <p className="text-sm text-neutral-600 mt-1 line-clamp-2">
                {system.generalDescription}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Upload Zone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-neutral-300 hover:border-primary-400 hover:bg-neutral-50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragActive ? 'text-primary-600' : 'text-neutral-400'}`} />
            <p className="text-sm font-medium text-neutral-900 mb-1">
              {isDragActive ? 'Drop files here' : 'Drag & drop files'}
            </p>
            <p className="text-xs text-neutral-600">
              or click to browse • CSV, Excel, JSON
            </p>
          </div>

          {/* Uploading Files */}
          {isUploading && (
            <div className="space-y-2 mb-4">
              {(uploadingFiles[system.name] || []).map((fileName) => (
                <div key={fileName} className="flex items-center gap-3 p-2 bg-blue-50 border border-blue-200 rounded">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="flex-1 text-sm text-neutral-900">{fileName}</span>
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ))}
            </div>
          )}

          {/* Uploaded Files List */}
          {systemFiles.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold text-neutral-700 mb-2">
                Uploaded Files ({systemFiles.length})
              </h4>
              <div className="space-y-3">
                {systemFiles.map((file) => {
                  const isExpanded = expandedFileId === file.fileId;
                  const isAnalyzing = analyzingFiles[file.fileId];
                  const analysis = fileAnalyses[file.fileId];
                  const error = analysisErrors[file.fileId];

                  return (
                    <div key={file.fileId} className="border border-green-200 rounded-lg overflow-hidden">
                      {/* File Summary Row */}
                      <div className="flex items-center gap-3 p-3 bg-green-50">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">{file.filename}</p>
                          <p className="text-xs text-neutral-600">
                            {formatFileSize(file.fileSize)}
                            {file.columnCount && ` • ${file.columnCount} columns`}
                            {file.rowCount && ` • ${file.rowCount} rows`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            onClick={() => handleViewAnalysis(file)}
                            className="text-primary-600 text-xs px-2 py-1"
                            title={isExpanded ? "Collapse analysis" : "View AI analysis"}
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => deleteMutation.mutate(file.fileId)}
                            loading={deleteMutation.isPending}
                            className="text-red-600 hover:bg-red-50 px-2 py-1"
                            title="Delete file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Analysis View */}
                      {isExpanded && (
                        <div className="p-4 bg-white border-t border-green-200">
                          {isAnalyzing && (
                            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <Sparkles className="w-5 h-5 text-blue-600 animate-pulse" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-neutral-900">
                                  Analyzing {file.filename}...
                                </p>
                                <p className="text-xs text-neutral-600">
                                  Identifying relevant columns and suggesting Neo4j mappings
                                </p>
                              </div>
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}

                          {!isAnalyzing && error && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-red-900 mb-1">
                                    Analysis Failed
                                  </p>
                                  <p className="text-xs text-red-700 mb-3">
                                    {error}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleReanalyze(file)}
                                    className="border-red-300 text-red-700 hover:bg-red-100"
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Re-analyze
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {!isAnalyzing && !error && !analysis && (
                            <div className="text-center py-8 text-neutral-500">
                              <Sparkles className="w-8 h-8 mx-auto mb-2 text-neutral-400" />
                              <p className="text-sm">No analysis available yet</p>
                            </div>
                          )}

                          {!isAnalyzing && analysis && schemaData?.schema && (
                            <FileAnalysisCard
                              caseId={ caseId}
                              fileId={file.fileId}
                              analysis={analysis}
                              schema={schemaData.schema}
                              onSchemaRefresh={() => {
                                // Invalidate all schema queries to refresh dropdown options and visualization
                                queryClient.invalidateQueries({ queryKey: ['schema', caseId] });
                                queryClient.invalidateQueries({ queryKey: ['schemaDesign', caseId] });
                              }}
                              onAcceptMapping={async (fileName, columnMappings) => {
                                try {
                                  const response = await fetch(
                                    `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/${file.fileId}/analysis/accept`,
                                    {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                      },
                                      body: JSON.stringify({
                                        caseId: caseId,
                                        fileName: fileName,
                                        columnMappings: columnMappings,
                                      }),
                                    }
                                  );

                                  if (!response.ok) {
                                    const errorData = await response.json();
                                    throw new Error(errorData.detail || 'Failed to save mappings');
                                  }

                                  toast.success('Column mappings accepted and saved');
                                  console.log('✅ Accepted mappings for', fileName);

                                  // Collapse and remove from local state
                                  setExpandedFileId(null);
                                  setFileAnalyses(prev => {
                                    const updated = { ...prev };
                                    delete updated[file.fileId];
                                    return updated;
                                  });
                                } catch (error: any) {
                                  console.error('Failed to accept mappings:', error);
                                  toast.error(`Failed to save mappings: ${error.message}`);
                                }
                              }}
                              onRejectMapping={async (fileName) => {
                                try {
                                  const response = await fetch(
                                    `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/${file.fileId}/analysis`,
                                    {
                                      method: 'DELETE',
                                    }
                                  );

                                  if (!response.ok) {
                                    const errorData = await response.json();
                                    throw new Error(errorData.detail || 'Failed to delete analysis');
                                  }

                                  console.log('✅ Rejected analysis for', fileName);
                                  setExpandedFileId(null);
                                  setFileAnalyses(prev => {
                                    const updated = { ...prev };
                                    delete updated[file.fileId];
                                    return updated;
                                  });
                                  toast.info('Analysis rejected and discarded');
                                } catch (error: any) {
                                  console.error('Failed to reject analysis:', error);
                                  setExpandedFileId(null);
                                  setFileAnalyses(prev => {
                                    const updated = { ...prev };
                                    delete updated[file.fileId];
                                    return updated;
                                  });
                                  toast.info('Analysis discarded');
                                }
                              }}
                              onUpdateMapping={(fileName, updatedMappings) => {
                                console.log('Updated mappings for', fileName, ':', updatedMappings);
                                setFileAnalyses(prev => ({
                                  ...prev,
                                  [file.fileId]: {
                                    ...prev[file.fileId],
                                    columns: updatedMappings,
                                  },
                                }));
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : !isUploading && (
            <div className="text-center py-4 text-sm text-neutral-500">
              No files uploaded yet
            </div>
          )}

        </CardBody>
      </Card>
    );
  };

  if (isLoadingLandscape) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (systems.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              No Enterprise Systems Found
            </h3>
            <p className="text-neutral-600 mb-6">
              You need to define enterprise systems in the Draft phase before uploading files.
            </p>
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back to Schema Design
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const hasFiles = files.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-l-4 border-l-blue-500">
        <CardBody>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Data Files Inventory
              </h3>
              <p className="text-neutral-600">
                Upload sample data files from each enterprise system. Files will be automatically tagged with their source system for easy organization.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Enterprise System Cards */}
      <div className="space-y-4">
        {systems.map(system => (
          <SystemUploadCard key={system.appId} system={system} />
        ))}
      </div>

      {/* Actions */}
      <Card>
        <CardFooter>
          <div className="flex items-center justify-between w-full">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back to Schema Design
            </Button>
            <Button
              onClick={onComplete}
              disabled={!hasFiles}
            >
              <CheckCircle2 className="w-4 h-4" />
              Continue to Field Analysis
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
